/*
	Template set builder for both Geolocation input types.

	`GeolocationSelector` and `GeolocationSelectorWithMap` render the same control — the map variant
	just adds a map — so both input types are built from this one place. The presentation (full picker
	vs compact bar) is chosen per field at render time by the `{~GeolocationBody:<Theme>~}` macro; see
	GeolocationBodies.js for the markup and HeadlightDynamicInputsBundle.js for the selection rules.

	The tabular Begin partial is the one piece that must stay literal HTML: the framework assembles a
	tabular cell as Begin + Mid + InformaryAddress + End, and the InformaryAddress partial injects
	`data-i-index` + `onchange` into whatever tag Begin leaves open. So Begin writes the two hidden
	informary inputs (latitude fully, longitude left open) and everything else lives in the body.
*/

/*
	The value binds as two informary hidden inputs with nested sub-addresses
	(<InformaryDataAddress>.Latitude / .Longitude), producing Container[row].<Field> = {Latitude,
	Longitude} — identical to the single-record shape. These stay the ONLY bound elements in a
	tabular/record-set cell in either presentation; the full picker's visible coordinate boxes are
	unbound editors the provider syncs to these.

	`data-validation-surface` points at the readout, which keeps the same id in both presentations,
	so the validation provider can resolve a visible surface for these hidden data-bearers.
*/
const _buildTabularBegin = (pInputType) => /*HTML*/`
	<!-- InputType ${pInputType} Tabular {~D:Record.Hash~} {~D:Record.DataType~} -->
	<input type="hidden" tabindex="-1"
		id="GEO-LAT-TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
		data-i-form="{~D:Context[0].formID~}"
		data-i-datum="{~D:Record.PictForm.InformaryDataAddress~}.Latitude"
		data-i-container="{~D:Record.PictForm.InformaryContainerAddress~}"
		data-i-index="{~D:Context[2].Key~}"
		data-validation-surface="GEO-DISPLAY-TABULAR-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
		value="" />
	<input type="hidden" tabindex="-1"
		id="GEO-LONG-TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
		data-i-form="{~D:Context[0].formID~}"
		data-i-datum="{~D:Record.PictForm.InformaryDataAddress~}.Longitude"
		data-i-container="{~D:Record.PictForm.InformaryContainerAddress~}"
		data-validation-surface="GEO-DISPLAY-TABULAR-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"`;

/** The single-record wrapper. The body macro fills in the presentation for this theme. */
const _buildStandardTemplate = (pInputType, pTheme) => /*HTML*/`
	<!-- InputType ${pInputType} {~D:Record.Hash~} {~D:Record.DataType~} -->
	<div {~D:Record.Macro.ControlAttr~} class="column {~ColumnWidth:Record~}${pTheme === 'Print' ? ' {~HideInPDF:Record~}' : ''} geolocation-input">
		<label class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
		{~GeolocationBody:${pTheme}~}
	</div>`;

/**
 * Build the Edit/Read/Print template entries for a Geolocation input type.
 * @param {string} pInputType - 'GeolocationSelector' or 'GeolocationSelectorWithMap'.
 * @return {{ Edit: Array<object>, Read: Array<object>, Print: Array<object> }}
 */
function buildGeolocationTemplates(pInputType)
{
	const tmpTabularBegin = _buildTabularBegin(pInputType);
	const tmpThemeEntries = (pTheme) => (
		[
			{
				HashPostfix: `-Template-Input-InputType-${pInputType}`,
				DefaultInputExtensions: [ 'GeolocationSelector' ],
				Template: _buildStandardTemplate(pInputType, pTheme),
			},
			{
				HashPostfix: `-TabularTemplate-Begin-Input-InputType-${pInputType}`,
				DefaultInputExtensions: [ 'GeolocationSelector' ],
				Template: tmpTabularBegin,
			},
			{
				// Closes the longitude input the Begin partial left open, then the cell body.
				HashPostfix: `-TabularTemplate-End-Input-InputType-${pInputType}`,
				DefaultInputExtensions: [ 'GeolocationSelector' ],
				Template: /*HTML*/`
		value="" />
	<div class="geolocation-input">{~GeolocationBody:${pTheme}~}</div>`,
			},
		]);
	return {
		Edit: tmpThemeEntries('Edit'),
		Read: tmpThemeEntries('Read'),
		Print: tmpThemeEntries('Print'),
	};
}

module.exports = { buildGeolocationTemplates };
